// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { css } from 'react-emotion';
import { components, elements, colors } from '@peachjar/components';
import { get } from 'lodash';
import { Mutation } from 'react-apollo';
import Checkbox from '@material-ui/core/Checkbox';

import CheckboxGroup from '../components/CheckboxGroup';
import CONFIRM_DISTRICT_MANUAL_SETUP_TASKS_MUTATION from '../../_shared/gql/confirmDistrictManualSetupTasks.mutation';
import { POLICIES_AND_RECOMMENDATIONS_PDF_LINK } from '../constants';

const {
  Buttons: { ButtonPrimaryLarge, ButtonSecondarySmall },
  Notifications: { notifyError },
} = components;
const { Headline2, Paragraph } = elements.typography;
const { prussian, mercury, gold } = colors;

const styles = {
  lineItem: css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    border: solid 2px ${gold};
    border-radius: 50%;
  `,
  listItemContents: css`
    padding: 1rem 3.5rem;
  `,
  checkboxBox: css`
    padding-top: 1.5rem;
  `,
  paddingTop: css`
    padding-top: 2rem;
  `,
};

const checkboxGroup1Labels = [
  'I confirm we sent out our parent email introduction to Peachjar.',
  'I confirm the Peachjar button is on all our websites.',
  'I confirm my staff is going through training.',
  'I confirm we sent out our community organization email introduction to Peachjar.',
];
const checkboxGroup2Labels = [
  "I confirm I have read and agree with Peachjar's Policies and Recommendations.",
];

const confirmTasks = (confirmSetupTasks, mutationVariables) => () => {
  try {
    confirmSetupTasks({ variables: mutationVariables });
  } catch (err) {
    console.log(`Error: ${err}`);
  }
};

type Props = {|
  +handleContinue: () => void,
  +onNotifyError: () => void,
  +ncesDistrictId: string,
  +setupProgress: {
    isSiteLinked: boolean,
    haveStaffBeenTrained: boolean,
    haveOrgsBeenEmailed: boolean,
    haveParentsBeenEmailed: boolean,
  },
|};

type State = {|
  isCheckboxGroup1Complete: boolean,
  isCheckboxGroup2Complete: boolean,
  hasPolicyButtonBeenClicked: boolean,
|};

class ConfirmSetup extends Component<Props, State> {
  state = {
    isCheckboxGroup1Complete: false,
    isCheckboxGroup2Complete: false,
    hasPolicyButtonBeenClicked: false,
  };

  toggleCheckboxGroup1Complete = isComplete =>
    this.setState({
      isCheckboxGroup1Complete: isComplete,
    });

  toggleCheckboxGroup2Complete = isComplete =>
    this.setState({
      isCheckboxGroup2Complete: isComplete,
    });

  handleState = (key, state) => () =>
    this.setState({
      [key]: state,
    });

  render() {
    // const queryVariables = { ncesId };
    const {
      isCheckboxGroup1Complete,
      isCheckboxGroup2Complete,
      hasPolicyButtonBeenClicked,
    } = this.state;
    const {
      handleContinue,
      ncesDistrictId,
      setupProgress,
      onNotifyError,
    } = this.props;

    // if each of these is true, then the district has already signed off. Thus, read-only checkboxes.
    const isReadOnly =
      !!get(setupProgress, 'isSiteLinked') &&
      !!get(setupProgress, 'haveStaffBeenTrained') &&
      !!get(setupProgress, 'haveOrgsBeenEmailed') &&
      !!get(setupProgress, 'haveParentsBeenEmailed');

    const variables = {
      input: {
        ncesDistrictId,
      },
    };

    return (
      <div
        style={{
          backgroundColor: 'white',
          padding: '3rem',
          borderRadius: '3px',
        }}
      >
        <div>
          <Headline2 style={{ marginBottom: '1rem' }}>
            You have reached the end of the onboarding process!
          </Headline2>
          <Paragraph>
            Once you have confirmed the following steps we will verify your
            information. After you have been verified you will receive an email
            notifying you that you can go live.
          </Paragraph>
        </div>

        <div className={styles.paddingTop}>
          <div className="layout-row layout-align-start-center">
            <div className={styles.lineItem}>1</div>
            <Headline2 style={{ marginBottom: '0', marginLeft: '2rem' }}>
              Confirm Setup
            </Headline2>
          </div>

          <div className={styles.listItemContents}>
            <CheckboxGroup
              toggleCheckboxGroupComplete={this.toggleCheckboxGroup1Complete}
              labels={checkboxGroup1Labels}
              uniqueIdModifier="tasks"
              isReadOnly={isReadOnly}
            />
          </div>
        </div>

        <div className={styles.paddingTop}>
          <div className="layout-row layout-align-start-center">
            <div className={styles.lineItem}>2</div>
            <Headline2 style={{ marginBottom: '0', marginLeft: '2rem' }}>
              Confirm Policies and Recommendations
            </Headline2>
          </div>
          <div className={styles.listItemContents}>
            <p>
              <Paragraph>
                Please read the following Policies and Recommendations.
              </Paragraph>
            </p>

            <a
              href={POLICIES_AND_RECOMMENDATIONS_PDF_LINK}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ButtonSecondarySmall
                data-testid="click-viewPoliciesAndRecommendations"
                onClick={this.handleState('hasPolicyButtonBeenClicked', true)}
              >
                View Policies and Recommendations
              </ButtonSecondarySmall>
            </a>

            <div className={styles.checkboxBox}>
              <CheckboxGroup
                toggleCheckboxGroupComplete={this.toggleCheckboxGroup2Complete}
                labels={checkboxGroup2Labels}
                disabled={!hasPolicyButtonBeenClicked}
                uniqueIdModifier="agreeToPolicies"
                isReadOnly={isReadOnly}
              />
            </div>
          </div>
        </div>

        <Mutation
          mutation={CONFIRM_DISTRICT_MANUAL_SETUP_TASKS_MUTATION}
          onCompleted={handleContinue}
          onError={onNotifyError}
        >
          {(confirmSetupTasks, { loading, error, data = {} }) => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '1rem',
                marginRight: '1rem',
              }}
            >
              <ButtonPrimaryLarge
                data-testid="submit-confirmSetup"
                disabled={
                  !isReadOnly &&
                  (!isCheckboxGroup1Complete ||
                    !isCheckboxGroup2Complete ||
                    !hasPolicyButtonBeenClicked)
                }
                onClick={
                  isReadOnly
                    ? handleContinue
                    : confirmTasks(confirmSetupTasks, variables)
                }
              >
                Continue
              </ButtonPrimaryLarge>
            </div>
          )}
        </Mutation>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  onNotifyError: error => dispatch(notifyError(error.message)),
});

export default connect(
  null,
  mapDispatchToProps
)(ConfirmSetup);



// WEBPACK FOOTER //
// ./src/Onboarding/Setup/confirm/containers/ConfirmSetupTab.jsx